import { CheckCheck, Loader2, X } from "lucide-react";
import type { Identifier } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";

import { cn } from "@/lib/utils";

import { KIND_ICON } from "./kindIcon";
import { groupMaterials, type Material } from "./materials";
import { useMaterials } from "./useMaterials";

interface MaterialPickerProps {
  /** Materials already sent to this client (marked, still sendable). */
  sentIds: Set<string>;
  /** False outside WhatsApp's 24 h window: files cannot be sent then. */
  canSend: boolean;
  onSend: (material: Material) => Promise<void>;
  onClose: () => void;
}

/** The materials library inside a WhatsApp conversation: one click sends a file. */
export const MaterialPicker = ({
  sentIds,
  canSend,
  onSend,
  onClose,
}: MaterialPickerProps) => {
  const { materials, isPending } = useMaterials();
  const [sendingId, setSendingId] = useState<Identifier | null>(null);
  const groups = groupMaterials(materials);

  const send = async (material: Material) => {
    setSendingId(material.id);
    try {
      await onSend(material);
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="flex max-h-72 min-h-0 flex-col gap-3 overflow-y-auto rounded-xl border bg-muted/30 p-2">
      <div className="flex items-center justify-between gap-2 px-1 text-[11px] text-muted-foreground">
        <span>
          {canSend
            ? "Un clic y se envía al cliente"
            : "Podrás enviar material en cuanto el cliente conteste"}
        </span>
        <span className="flex items-center gap-2">
          <Link to="/whatsapp/material" className="underline">
            Ver biblioteca
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 hover:bg-muted hover:text-foreground"
            aria-label="Cerrar material"
            title="Cerrar"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      </div>
      {isPending ? (
        <p className="px-1 text-xs text-muted-foreground">Cargando…</p>
      ) : !groups.length ? (
        <p className="px-1 text-xs text-muted-foreground">
          Todavía no hay material subido.
        </p>
      ) : (
        groups.map((group) => (
          <div key={group.value} className="flex flex-col gap-1">
            <span className="px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {group.label}
            </span>
            {group.items.map((material) => {
              const Icon = KIND_ICON[material.kind];
              const isSent = sentIds.has(String(material.id));
              const isSending = sendingId === material.id;
              return (
                <button
                  key={material.id}
                  type="button"
                  disabled={!canSend || sendingId != null}
                  onClick={() => send(material)}
                  title={material.note ?? undefined}
                  className={cn(
                    "flex items-center gap-2 rounded-lg bg-background px-2 py-1.5 text-left text-sm transition-colors",
                    canSend
                      ? "hover:bg-lime-100 dark:hover:bg-lime-950/40"
                      : "cursor-not-allowed opacity-60",
                  )}
                >
                  {isSending ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                  ) : (
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate">{material.title}</span>
                    {material.note ? (
                      <span className="truncate text-[11px] text-muted-foreground">
                        {material.note}
                      </span>
                    ) : null}
                  </span>
                  {isSent ? (
                    <span className="inline-flex shrink-0 items-center gap-0.5 text-[11px] text-sky-600">
                      <CheckCheck className="h-3.5 w-3.5" />
                      enviado
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
};
